<template>
  <div id="contenedor-editar-obra">
    <v-form ref="editarObra" v-model="esFormularioValido">
      <v-card>
        <v-card-title class="secondary--text">
          Editar Obra
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="cerrar">
            <v-icon class="secondary--text" large>highlight_off</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Sección de Campos Requeridos -->
        <v-card-subtitle class="pb-2">
          <v-icon color="error" small class="mr-2">info</v-icon>
          <strong>Campos Requeridos</strong>
          <span class="text-caption ml-2 grey--text">
            Complete todos los campos marcados con (*)
          </span>
        </v-card-subtitle>

        <v-card-text>
          <!-- Información Básica Requerida -->
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <custom-select
                v-model="obra.ejercicio"
                label="Ejercicio*"
                :items="itemsejercicio"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
              >
              </custom-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <custom-text-field
                v-model="obra.claveProyecto"
                label="Clave de proyecto*"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                placeholder="Ej: NP-2025-001"
                v-on:keypress.enter="mixActualizarObra"
              >
              </custom-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <custom-text-field
                v-model="obra.clave"
                label="Clave de obra*"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                placeholder="Ej: 25-SEC-001"
                v-on:keypress.enter="mixActualizarObra"
              >
              </custom-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <custom-select
                v-model="obra.idTipo"
                label="Tipo de Obra*"
                :items="tiposObras"
                :rules="[mixReglas.requerido]"
                item-text="descripcion"
                item-value="id"
                required
                outlined
                dense
              >
              </custom-select>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <custom-select
                v-model="obra.idSubtipo"
                label="SubTipo de Obra*"
                :items="getSubTipoObraPorTipo"
                item-text="descripcion"
                item-value="id"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                :disabled="!obra.idTipo"
                :placeholder="obra.idTipo ? 'Seleccione subtipo' : 'Primero seleccione tipo'"
              >
              </custom-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <custom-text-area
                v-model="obra.meta"
                label="Meta*"
                :rules="[mixReglas.requerido]"
                auto-grow
                required
                outlined
                rows="2"
                placeholder="Describa la meta u objetivo de la obra..."
                v-on:keypress.enter="mixActualizarObra"
              >
              </custom-text-area>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6" md="6">
              <custom-select
                v-model="obra.idPrograma"
                label="Programa*"
                :items="programasInversiones"
                :rules="[mixReglas.requerido]"
                item-text="nombre"
                item-value="id"
                required
                outlined
                dense
              >
              </custom-select>
            </v-col>
          </v-row>

          <!-- Ubicación Básica Requerida -->
          <v-row>
            <v-col cols="12" sm="6" md="4">
              <custom-select
                v-model="obra.ubicacion.idMunicipio"
                label="Municipio*"
                :items="municipios"
                item-text="nombre"
                item-value="id"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
              >
              </custom-select>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <custom-select
                v-model="obra.ubicacion.idLocalidad"
                label="Localidad*"
                :items="getLocalidadesPorMunicipio"
                item-text="descripcion"
                item-value="id"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                :disabled="!obra.ubicacion.idMunicipio"
                :placeholder="obra.ubicacion.idMunicipio ?
                  'Seleccione localidad' : 'Primero seleccione municipio'"
              >
              </custom-select>
            </v-col>
            <v-col cols="12" sm="6" md="4">
              <custom-select
                v-model="obra.ubicacion.idColonia"
                label="Colonia*"
                :items="getMunicipiosPorLocalidad"
                item-text="descripcion"
                item-value="id"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                :disabled="!obra.ubicacion.idLocalidad"
                :placeholder="obra.ubicacion.idLocalidad ?
                  'Seleccione colonia' : 'Primero seleccione localidad'"
              >
              </custom-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <custom-text-field
                v-model="obra.ubicacion.ubicacion"
                label="Ubicación*"
                :rules="[mixReglas.requerido]"
                required
                outlined
                dense
                placeholder="Ej: Calle Principal #123, Col. Centro"
                v-on:keypress.enter="mixActualizarObra"
              >
              </custom-text-field>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Sección de Campos Opcionales -->
          <div class="d-flex align-center mb-3">
            <v-icon color="primary" class="mr-2">settings</v-icon>
            <span class="subtitle-1 font-weight-medium">Información Opcional</span>
            <v-spacer></v-spacer>
            <v-btn
              text
              small
              color="primary"
              @click="mostrarCamposOpcionales = !mostrarCamposOpcionales"
            >
              {{ mostrarCamposOpcionales ? 'Ocultar' : 'Mostrar' }} campos opcionales
              <v-icon right>{{ mostrarCamposOpcionales ? 'expand_less' : 'expand_more' }}</v-icon>
            </v-btn>
          </div>

          <!-- Campos Opcionales Básicos -->
          <v-expand-transition>
            <div v-show="mostrarCamposOpcionales">
              <v-row>
                <v-col cols="12" sm="6" md="4">
                  <custom-select
                    v-model="obra.etapa"
                    label="Etapa"
                    :items="itemsetapa"
                    item-text="etapa"
                    item-value="valor"
                    outlined
                    dense
                  >
                  </custom-select>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <custom-select
                    v-model="obra.tipoProyecto"
                    label="Tipo de Proyecto"
                    :items="itemstipodeproyecto"
                    item-text="proyecto"
                    item-value="valor"
                    outlined
                    dense
                  >
                  </custom-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="4" md="4">
                  <custom-date-picker
                    v-model="obra.fechaSolicitudRecursos"
                    label="Fecha de Solicitud de Recursos"
                    outlined
                    dense
                  >
                  </custom-date-picker>
                </v-col>
                <v-col cols="12" sm="4" md="4">
                  <custom-date-picker
                    v-model="obra.fechaProbableInicio"
                    label="Fecha Probable de Inicio"
                    outlined
                    dense
                  >
                  </custom-date-picker>
                </v-col>
                <v-col cols="12" sm="4" md="4">
                  <custom-date-picker
                    v-model="obra.fechaProbableTermino"
                    label="Fecha Probable de Termino"
                    outlined
                    dense
                  >
                  </custom-date-picker>
                </v-col>
              </v-row>

              <!-- Información Administrativa -->
              <v-row class="mt-3">
                <v-col cols="12">
                  <div class="d-flex align-center mb-3">
                    <v-icon color="info" class="mr-2">folder</v-icon>
                    <span class="subtitle-2 font-weight-medium">
                      Información Administrativa
                    </span>
                  </div>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <custom-text-field
                    v-model="obra.numeroCarpeta"
                    label="Número de Carpeta"
                    outlined
                    dense
                    placeholder="Ej: CARP-2025-001"
                    v-on:keypress.enter="mixActualizarObra"
                  >
                  </custom-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <custom-text-field
                    v-model="obra.numeroSesionAprobacion"
                    label="Número de Sesión de Aprobación"
                    outlined
                    dense
                    placeholder="Ej: SES-001-2025"
                    v-on:keypress.enter="mixActualizarObra"
                  >
                  </custom-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <custom-text-field
                    v-model="obra.numeroObraCecop"
                    label="Número de Obra CECOP"
                    outlined
                    dense
                    placeholder="Ej: CECOP-2025-001"
                    v-on:keypress.enter="mixActualizarObra"
                  >
                  </custom-text-field>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                  <custom-text-field
                    v-model="obra.numeroLiberacionCecop"
                    label="Número de Liberación CECOP"
                    outlined
                    dense
                    placeholder="Ej: LIB-CECOP-001"
                    v-on:keypress.enter="mixActualizarObra"
                  >
                  </custom-text-field>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6" md="6">
                  <custom-select
                    v-model="obra.idGrupoObra"
                    label="Grupo de Obra"
                    :items="getGruposObras"
                    item-text="descripcion"
                    item-value="id"
                    outlined
                    dense
                    placeholder="Seleccione grupo de obra"
                  >
                  </custom-select>
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <custom-select
                    v-model="obra.idSubgrupoObra"
                    label="Subgrupo de Obra"
                    :items="getSubgruposObrasPorGrupo"
                    item-text="descripcion"
                    item-value="id"
                    outlined
                    dense
                    :disabled="!obra.idGrupoObra"
                    :placeholder="obra.idGrupoObra ?
                      'Seleccione subgrupo' : 'Primero seleccione grupo'"
                  >
                  </custom-select>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" sm="6" md="6">
                  <custom-select
                    v-model="obra.idSubprograma"
                    label="SubPrograma"
                    :items="getSubprogramas"
                    item-text="nombre"
                    item-value="id"
                    outlined
                    dense
                    placeholder="Seleccione subprograma"
                  >
                  </custom-select>
                </v-col>
                <v-col cols="12" sm="6" md="6">
                  <custom-select
                    v-model="obra.idPartidaContable"
                    label="Partida contable"
                    :items="getPartidasContables"
                    item-text="descripcion"
                    item-value="id"
                    outlined
                    dense
                    placeholder="Seleccione partida contable"
                  >
                  </custom-select>
                </v-col>
              </v-row>
            </div>
          </v-expand-transition>

          <!-- Secciones Expandibles para Datos Detallados -->
          <v-expansion-panels
            accordion
            flat
            class="mt-4"
            v-show="mostrarCamposOpcionales"
          >
            <!-- Ubicación Detallada -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <template v-slot:default="{ open }">
                  <v-row no-gutters>
                    <v-col cols="4" class="d-flex align-center">
                      <v-icon class="mr-2" color="primary">place</v-icon>
                      <strong>Ubicación Detallada</strong>
                    </v-col>
                    <v-col cols="8" class="text--secondary">
                      <v-fade-transition leave-absolute>
                        <span v-if="open" key="0">
                          Coordenadas, tipo de localidad y detalles del sitio
                        </span>
                        <span v-else key="1">
                          {{ getUbicacionResumen }}
                        </span>
                      </v-fade-transition>
                    </v-col>
                  </v-row>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <custom-select
                      v-model="obra.ubicacion.tipoLocalidad"
                      label="Tipo de Localidad"
                      :items="itemstipolocalidad"
                      outlined
                      dense
                    >
                    </custom-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <custom-text-field
                      v-model="obra.ubicacion.latitud"
                      label="Latitud"
                      outlined
                      dense
                      placeholder="Ej: 19.4326"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <custom-text-field
                      v-model="obra.ubicacion.longitud"
                      label="Longitud"
                      outlined
                      dense
                      placeholder="Ej: -99.1332"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="8" md="8">
                    <custom-text-field
                      v-model="obra.ubicacion.sitio"
                      label="Sitio"
                      outlined
                      dense
                      placeholder="Descripción del sitio específico"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                  <v-col cols="12" sm="4" md="4">
                    <v-switch
                      v-model="obra.ubicacion.cabeceraMunicipial"
                      inset
                      label="¿Es Cabecera Municipal?"
                      color="primary"
                    >
                    </v-switch>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Impacto y Metas -->
            <v-expansion-panel>
              <v-expansion-panel-header>
                <template v-slot:default="{ open }">
                  <v-row no-gutters>
                    <v-col cols="4" class="d-flex align-center">
                      <v-icon class="mr-2" color="success">trending_up</v-icon>
                      <strong>Impacto y Metas</strong>
                    </v-col>
                    <v-col cols="8" class="text--secondary">
                      <v-fade-transition leave-absolute>
                        <span v-if="open" key="0">
                          Beneficiarios, metas físicas, avances y justificaciones
                        </span>
                        <span v-else key="1">
                          {{ getImpactoResumen }}
                        </span>
                      </v-fade-transition>
                    </v-col>
                  </v-row>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-row>
                  <v-col cols="12" sm="6" md="4">
                    <custom-text-field
                      v-model.number="obra.impacto.beneficiados"
                      label="Beneficiados"
                      type="number"
                      outlined
                      dense
                      placeholder="Número de personas"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <custom-text-field
                      v-model.number="obra.impacto.metaFisica"
                      label="Meta Física"
                      type="number"
                      outlined
                      dense
                      placeholder="Cantidad"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <custom-text-field
                      v-model="obra.impacto.metaFisicaUnidad"
                      label="Unidad Meta Física"
                      outlined
                      dense
                      placeholder="Ej: metros, piezas, etc."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" sm="6" md="6">
                    <custom-text-field
                      v-model.number="obra.impacto.avanceFisicoActual"
                      suffix="%"
                      label="Avance Físico Actual"
                      type="number"
                      outlined
                      dense
                      min="0"
                      max="100"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                  <v-col cols="12" sm="6" md="6">
                    <custom-text-field
                      v-model.number="obra.impacto.avanceProgramadoCierre"
                      suffix="%"
                      label="Avance Programado Cierre"
                      type="number"
                      outlined
                      dense
                      min="0"
                      max="100"
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <custom-text-area
                      v-model="obra.impacto.justificacionTecnica"
                      label="Justificación Técnica"
                      outlined
                      auto-grow
                      rows="2"
                      placeholder="Explique la justificación técnica de la obra..."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-area>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <custom-text-area
                      v-model="obra.impacto.justificacionSocial"
                      label="Justificación Social"
                      outlined
                      auto-grow
                      rows="2"
                      placeholder="Explique el impacto social esperado..."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-area>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <custom-text-area
                      v-model="obra.impacto.problematica"
                      label="Problemática"
                      outlined
                      auto-grow
                      rows="2"
                      placeholder="Describa la problemática que resuelve la obra..."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-area>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <custom-text-area
                      v-model="obra.impacto.descripcionTotalObra"
                      label="Descripción Total de la Obra"
                      outlined
                      auto-grow
                      rows="3"
                      placeholder="Descripción completa de la obra..."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-area>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <custom-text-area
                      v-model="obra.impacto.conceptosEjecutar"
                      label="Conceptos a Ejecutar"
                      outlined
                      auto-grow
                      rows="2"
                      placeholder="Describa los conceptos que se ejecutarán..."
                      v-on:keypress.enter="mixActualizarObra"
                    >
                    </custom-text-area>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <custom-button
            @click="cerrar"
            tipo="formCancelar"
            label="Cancelar"
            class="mr-3">
          </custom-button>
          <custom-button
            @click="mixActualizarObra"
            :disabled="!esFormularioValido || enviandoFormulario"
            :loading="enviandoFormulario"
            tipo="formAceptar"
            label="Actualizar Obra">
          </custom-button>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>

<script>
import LOCALIDADES_POR_MUNICIPIO from '@/graphql/querys/administracion/catalogos/localidades/catalogoLocalidadesPorMunicipio.gql';
import MUNICIPIOS_POR_LOCALIDAD from '@/graphql/querys/administracion/catalogos/colonias/catalogoColoniasPorLocalidad.gql';
import GRUPOS_OBRAS from '@/graphql/querys/administracion/catalogos/grupos.obras/catalogoGruposObras.gql';
import SUBGRUPOS_OBRAS from '@/graphql/querys/administracion/catalogos/subgrupos.obras/catalogoSubgruposObrasPorGrupo.gql';
import PARTIDAS_CONTABLES from '@/graphql/querys/administracion/catalogos/partidas.contables/catalogoPartidasContables.gql';
import SUB_PROGRAMAS from '@/graphql/querys/administracion/catalogos/subprogramas/catalogoSubprogramas.gql';
import CustomTextField from '@/components/inputs/TextField.vue';
import CustomSelect from '@/components/inputs/Select.vue';
import CustomTextArea from '@/components/inputs/TextArea.vue';
import CustomDatePicker from '@/components/inputs/DatePicker.vue';
import CustomButton from '@/components/buttons/Button.vue';
import mixObras from './mixObras';

export default {
  name: 'ObrasEditar',
  mixins: [mixObras],
  props: {
    obraEditar: Object,
  },
  components: {
    CustomTextField,
    CustomSelect,
    CustomTextArea,
    CustomDatePicker,
    CustomButton,
  },
  data() {
    return {
      esFormularioValido: false,
      enviandoFormulario: false,
      mostrarCamposOpcionales: false,
      itemsejercicio: [2021, 2022, 2023, 2024, 2025],
      itemsetapa: [
        { etapa: 'Nueva', valor: 1 },
        { etapa: 'Ampliación', valor: 2 },
        { etapa: 'Rehabilitación', valor: 3 },
      ],
      itemstipodeproyecto: [
        { proyecto: 'Social', valor: 1 },
        { proyecto: 'Productivo', valor: 2 },
        { proyecto: 'Apoyo', valor: 3 },
      ],
      itemstipolocalidad: ['Urbana', 'Rural'],
      obra: {
        claveProyecto: (this.obraEditar && this.obraEditar.claveProyecto) || '',
        idPrograma: this.obraEditar.idPrograma, // (Catalogo)
        idTipo: this.obraEditar.idTipo, // (Catalogo)
        idSubtipo: this.obraEditar.idSubtipo, // (Catalogo)
        ejercicio: this.obraEditar.ejercicio,
        clave: this.obraEditar.clave,
        meta: this.obraEditar.meta,
        etapa: this.obraEditar.etapa, // (1=> Nueva, 2 => Amplicación, 3 => Rehabilitación)
        tipoProyecto: this.obraEditar.tipoProyecto, // (1 => Social, 2 => Productivo, 3 => Apoyo)
        fechaSolicitudRecursos: this.obraEditar.fechaSolicitudRecursos,
        fechaProbableInicio: this.obraEditar.fechaProbableInicio,
        fechaProbableTermino: this.obraEditar.fechaProbableTermino,
        numeroCarpeta: this.obraEditar.numeroCarpeta || null,
        numeroSesionAprobacion: this.obraEditar.numeroSesionAprobacion || null,
        idPartidaContable: this.obraEditar.idPartidaContable || '',
        idGrupoObra: this.obraEditar.idGrupoObra || '',
        idSubgrupoObra: this.obraEditar.idSubgrupoObra || '',
        idSubprograma: this.obraEditar.idSubprograma || null,
        numeroObraCecop: this.obraEditar.numeroObraCecop || '',
        numeroLiberacionCecop: this.obraEditar.numeroLiberacionCecop || '',
        ubicacion: {
          idMunicipio: this.obraEditar.ubicacion.idMunicipio, // (Catalogo)
          idLocalidad: this.obraEditar.ubicacion.idLocalidad || null, // (Catalogo)
          idColonia: this.obraEditar.ubicacion.idColonia || null, // (Catalogo)
          ubicacion: this.obraEditar.ubicacion.ubicacion,
          latitud: this.obraEditar.ubicacion.latitud,
          longitud: this.obraEditar.ubicacion.longitud,
          sitio: this.obraEditar.ubicacion.sitio,
          cabeceraMunicipial: this.obraEditar.ubicacion.cabeceraMunicipial,
          tipoLocalidad: this.obraEditar.ubicacion.tipoLocalidad,
        },
        impacto: {
          beneficiados: this.obraEditar.impacto.beneficiados,
          metaFisica: this.obraEditar.impacto.metaFisica,
          metaFisicaUnidad: this.obraEditar.impacto.metaFisicaUnidad,
          justificacionTecnica: this.obraEditar.impacto.justificacionTecnica,
          justificacionSocial: this.obraEditar.impacto.justificacionSocial,
          problematica: this.obraEditar.impacto.problematica,
          avanceFisicoActual: this.obraEditar.impacto.avanceFisicoActual,
          avanceProgramadoCierre: this.obraEditar.impacto.avanceProgramadoCierre,
          descripcionTotalObra: this.obraEditar.impacto.descripcionTotalObra,
          conceptosEjecutar: this.obraEditar.impacto.conceptosEjecutar,
        },
      },
    };
  },
  apollo: {
    catalogoLocalidadesPorMunicipio: {
      query: LOCALIDADES_POR_MUNICIPIO,
      variables() {
        return {
          idMunicipio: this.obra.ubicacion.idMunicipio,
        };
      },
      skip() {
        return !this.obra.ubicacion.idMunicipio;
      },
    },
    catalogoColoniasPorLocalidad: {
      query: MUNICIPIOS_POR_LOCALIDAD,
      variables() {
        return {
          idLocalidad: this.obra.ubicacion.idLocalidad,
        };
      },
      skip() {
        return !this.obra.ubicacion.idLocalidad;
      },
    },
    catalogoGruposObras: {
      query: GRUPOS_OBRAS,
    },
    catalogoSubgruposObrasPorGrupo: {
      query: SUBGRUPOS_OBRAS,
      variables() {
        return {
          idGrupoObra: this.obra.idGrupoObra,
        };
      },
      skip() {
        return !this.obra.idGrupoObra;
      },
    },
    catalogoPartidasContables: {
      query: PARTIDAS_CONTABLES,
    },
    catalogoSubprogramas: {
      query: SUB_PROGRAMAS,
    },
  },
  computed: {
    getLocalidadesPorMunicipio() {
      if (!this.catalogoLocalidadesPorMunicipio) return [];
      return this.catalogoLocalidadesPorMunicipio;
    },
    getMunicipiosPorLocalidad() {
      if (!this.catalogoColoniasPorLocalidad) return [];
      return this.catalogoColoniasPorLocalidad;
    },
    getGruposObras() {
      if (!this.catalogoGruposObras) return [];
      return this.catalogoGruposObras;
    },
    getSubgruposObrasPorGrupo() {
      if (!this.catalogoSubgruposObrasPorGrupo) return [];
      return this.catalogoSubgruposObrasPorGrupo;
    },
    getUbicacionResumen() {
      const partes = [];
      if (this.obra.ubicacion.tipoLocalidad) partes.push('Tipo de localidad definido');
      if (this.obra.ubicacion.latitud && this.obra.ubicacion.longitud) {
        partes.push('Coordenadas definidas');
      }
      if (this.obra.ubicacion.sitio) partes.push('Sitio especificado');
      if (this.obra.ubicacion.cabeceraMunicipial) partes.push('Es cabecera municipal');
      return partes.length > 0 ? partes.join(', ') : 'Sin información adicional';
    },
    getImpactoResumen() {
      const partes = [];
      if (this.obra.impacto.beneficiados > 0) {
        partes.push(`${this.obra.impacto.beneficiados} beneficiados`);
      }
      if (this.obra.impacto.metaFisica > 0) {
        partes.push(`Meta: ${this.obra.impacto.metaFisica}`);
      }
      if (this.obra.impacto.avanceFisicoActual > 0) {
        partes.push(`Avance: ${this.obra.impacto.avanceFisicoActual}%`);
      }
      return partes.length > 0 ? partes.join(', ') : 'Sin información de impacto';
    },
    getPartidasContables() {
      if (!this.catalogoPartidasContables) return [];
      return this.catalogoPartidasContables.map(({ id, numero, descripcion }) => ({
        id,
        descripcion: `${numero} - ${descripcion}`,
      }));
    },
    getSubprogramas() {
      if (!this.catalogoSubprogramas) return [];
      return this.catalogoSubprogramas;
    },
  },
  methods: {
    cerrar() {
      this.limpiarFormulario();
      this.$emit('cerrar');
    },
    limpiarFormulario() {
      this.$refs.editarObra.reset();
      this.mostrarCamposOpcionales = false;
    },
  },
};
</script>

<style scoped>
#contenedor-editar-obra {
  max-width: 100%;
}

.v-card {
  border-radius: 12px;
}

.v-card-title {
  font-weight: 600;
  font-size: 1.5rem;
}

.v-card-subtitle {
  background-color: #f5f5f5;
  border-left: 4px solid #1976d2;
  margin: 0 -16px;
  padding: 12px 16px;
}

.v-expansion-panel-header {
  min-height: 64px;
}

.v-expansion-panel-header:hover {
  background-color: #f8f9fa;
}

.v-expansion-panel-content {
  background-color: #fafafa;
}

/* Mejorar el espaciado de los campos */
.v-text-field--outlined,
.v-select--outlined,
.v-textarea--outlined {
  margin-bottom: 8px;
}

/* Estilo para campos requeridos */
.v-label--active {
  color: #1976d2 !important;
}

/* Indicador visual para campos opcionales */
.v-expansion-panel::before {
  box-shadow: none;
}

/* Mejorar botones */
.v-btn {
  text-transform: none;
  font-weight: 500;
}

/* Animaciones suaves */
.v-expand-transition-enter-active,
.v-expand-transition-leave-active {
  transition: all 0.3s ease;
}
</style>
